// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — which screen to come back to after a reload. Pure: no DOM, no storage.
//
// A reload — pulling down, the phone discarding a background tab, an update of the app — used to
// start on Home whatever was on screen. The shell remembers the VIEW here (tab, list or TODO, the
// list's segment and grouping, the scroll offset) in `sessionStorage` and hands it back on the next
// start of the same tab. Sheets and the search card are not views: they are half-finished actions,
// and reopening one unasked would be its own surprise.
//
// ⚠ Keyed by the INSTALLATION. A TODO id means nothing on another installation, and a phone paired
// with two must not open the other one's list on a TODO that does not exist there.
// ─────────────────────────────────────────────────────────────────────────────

import { type MobileState } from "home/app/mobile/store";
import { type ListScope, type TodoGrouping } from "home/app/mobile/types";

export interface SavedView {
    tenant: string;
    tab: "home" | "todos";
    todoView: "list" | "detail";
    selectedId: string | null;
    relation: ListScope;
    grouping: TodoGrouping;
    scrollY: number;
}

const GROUPINGS: readonly TodoGrouping[] = ["due", "status", "priority", "project"];

/** The installation a profile belongs to — host plus alias, the same address the header shows. */
export function viewTenantOf(profile: MobileState["profile"]): string {
    return profile?.tenantUrl || profile?.tenantHost || "";
}

/** What to remember for `state`, or null when there is nothing worth coming back to. */
export function viewToSave(state: MobileState, scrollY: number): SavedView | null {
    const tenant = viewTenantOf(state.profile);
    if (!state.signedIn || !tenant) return null;

    const detail = state.tab === "todos" && state.todoView === "detail" && Boolean(state.selectedId);
    return {
        tenant,
        tab: state.tab,
        todoView: detail ? "detail" : "list",
        selectedId: detail ? state.selectedId : null,
        relation: state.relation,
        grouping: state.grouping,
        scrollY: Math.max(0, Math.round(scrollY)),
    };
}

/**
 * The store patch that puts a remembered view back, for the installation now signed in — or `null`
 * when there is none, it is unreadable, or it belongs to another installation.
 *
 * Read defensively: it is whatever this tab stored, from any earlier version of the app.
 */
export function viewToRestore(raw: unknown, tenant: string): { patch: Partial<MobileState>; scrollY: number } | null {
    if (!raw || typeof raw !== "object" || !tenant) return null;
    const saved = raw as Partial<Record<keyof SavedView, unknown>>;
    if (saved.tenant !== tenant) return null;

    const tab = saved.tab === "todos" ? "todos" : "home";
    const selectedId = typeof saved.selectedId === "string" && saved.selectedId ? saved.selectedId : null;
    const detail = tab === "todos" && saved.todoView === "detail" && selectedId !== null;
    const patch: Partial<MobileState> = { tab, todoView: detail ? "detail" : "list" };
    if (detail) patch.selectedId = selectedId;
    if (saved.relation === "assignee" || saved.relation === "owner" || saved.relation === "all") {
        patch.relation = saved.relation;
    }
    if (GROUPINGS.includes(saved.grouping as TodoGrouping)) patch.grouping = saved.grouping as TodoGrouping;

    const scrollY =
        typeof saved.scrollY === "number" && Number.isFinite(saved.scrollY) ? Math.max(0, saved.scrollY) : 0;
    return { patch, scrollY };
}
